/**
 * Parses an amount string in rand (major units) to minor units (cents).
 *
 * Strips leading/trailing whitespace and comma thousands separators before
 * parsing, so both "1500.00" and "1,500.00" are accepted.
 *
 * Strict on format (not lenient like a bare `parseFloat`): the normalised value
 * must be digits with an optional fractional part of at least one digit. This
 * rejects empty, partial in-progress input ("14."), non-numeric ("12abc"), a
 * bare minus, and negatives - all returning null. Extra precision rounds to the
 * nearest cent ("1500.005" => 150001). Returns 0 for zero inputs.
 *
 * This is the symmetric counterpart to formatCurrency: minor units in from
 * the store, rand strings in from user input.
 *
 * Example: parseAmountToMinorUnits('1,500.00') => 150000
 *          parseAmountToMinorUnits('1500')      => 150000
 *          parseAmountToMinorUnits('14.')       => null  (partial input)
 *          parseAmountToMinorUnits('-100')      => null
 *          parseAmountToMinorUnits('')          => null
 */
export function parseAmountToMinorUnits(value) {
    const normalised = (value ?? '').replace(/\s/g, '').replace(/,/g, '');
    if (!/^\d+(\.\d+)?$/.test(normalised))
        return null;
    const num = parseFloat(normalised);
    if (!Number.isFinite(num))
        return null;
    return Math.round(num * 100);
}
/**
 * Signed sibling of parseAmountToMinorUnits: accepts an optional leading `-`,
 * so both money-in ("1,500.00") and money-out ("-1,500.00") CSV columns parse.
 *
 * Delegates the magnitude to parseAmountToMinorUnits, inheriting its exact
 * strictness and rounding (empty, partial "14.", non-numeric, and a bare "-"
 * all return null; extra precision rounds to the nearest cent). Returns 0 (never
 * -0) for a signed zero input.
 *
 * Example: parseSignedAmountToMinorUnits('-100.00') => -10000
 *          parseSignedAmountToMinorUnits('1,500.00') => 150000
 *          parseSignedAmountToMinorUnits('-')         => null  (bare sign)
 */
export function parseSignedAmountToMinorUnits(value) {
    const trimmed = (value ?? '').replace(/\s/g, '');
    const negative = trimmed.startsWith('-');
    const magnitude = parseAmountToMinorUnits(negative ? trimmed.slice(1) : trimmed);
    if (magnitude === null)
        return null;
    return negative ? -magnitude || 0 : magnitude;
}
/**
 * Normalise unsigned CSV numbers from period- or comma-decimal exports.
 * Comma-only groups of three are thousands ("1,250" => "1250"); one or
 * two trailing digits are decimals ("12,50" => "12.50"). With both marks,
 * the final mark is decimal and the other must form valid thousands groups.
 * Spaces are removed. Malformed cells return null rather than being truncated.
 */
export function normaliseNumericCell(value) {
    if (value === undefined)
        return null;
    const stripped = value.replace(/\s/g, '');
    if (stripped.includes(',') && stripped.includes('.')) {
        const decimal = stripped.lastIndexOf(',') > stripped.lastIndexOf('.') ? ',' : '.';
        const grouping = decimal === ',' ? '.' : ',';
        const [integer, fraction, extra] = stripped.split(decimal);
        if (extra !== undefined || !fraction || !/^\d+$/.test(fraction))
            return null;
        const groups = integer.split(grouping);
        if (!/^\d{1,3}$/.test(groups[0]) || !groups.slice(1).every(group => /^\d{3}$/.test(group)))
            return null;
        return `${groups.join('')}.${fraction}`;
    }
    if (/^\d{1,3}(,\d{3})+$/.test(stripped))
        return stripped.replace(/,/g, '');
    if (/^\d+,\d{1,2}$/.test(stripped))
        return stripped.replace(',', '.');
    return /^\d+(\.\d+)?$/.test(stripped) ? stripped : null;
}
/** Parse unsigned CSV money after normalising its decimal and grouping marks. */
export function parseCsvAmountToMinorUnits(value) {
    const normalised = normaliseNumericCell(value);
    return normalised === null ? null : parseAmountToMinorUnits(normalised);
}
/** Signed CSV money; a leading minus applies to the normalised magnitude. */
export function parseSignedCsvAmountToMinorUnits(value) {
    const stripped = (value ?? '').replace(/\s/g, '');
    const negative = stripped.startsWith('-');
    const magnitude = parseCsvAmountToMinorUnits(negative ? stripped.slice(1) : stripped);
    if (magnitude === null)
        return null;
    return negative ? -magnitude || 0 : magnitude;
}
/** Parse an en-GB money form field without rounding extra decimal places. */
export function parseMoneyInputToMinorUnits(value) {
    const trimmed = value.trim();
    if (!/^(\d+|\d{1,3}(,\d{3})+)(\.\d+)?$/.test(trimmed))
        return null;
    const normalised = normaliseNumericCell(trimmed);
    if (normalised === null || /\.\d{3,}$/.test(normalised))
        return null;
    return parseAmountToMinorUnits(normalised);
}
